import type { MrrTrendPoint } from '@ops/contracts'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  formatMetricValue,
  formatPeriodo,
  formatPeriodoCorto,
} from '../../lib/format'
import { CHART } from '../../ui'
import { Vacio } from '../comunes'

/**
 * Los dos gráficos de revenue del founder, con Recharts porque tienen ejes.
 *
 * `MrrLinea` es la tendencia de nivel (la columna derecha de la tarjeta héroe) y
 * `NetNewBarras` descompone el mes en MRR nuevo contra MRR perdido: la barra de
 * pérdida va **abajo del eje**, porque dibujarla para arriba junto a la de
 * ganancia hace que un mes malo parezca un mes con mucho movimiento.
 *
 * Los colores salen de `CHART`, que se lee del mismo preset que las clases de
 * Tailwind: no hay una paleta paralela para los gráficos.
 */
export interface MrrTrendProps {
  trend: readonly MrrTrendPoint[]
  /** Alto en px. Los gráficos del panel no crecen con la pantalla. */
  alto?: number
}

const EJE = { fontSize: 11, fill: CHART.eje }

function usd(valor: number): string {
  return formatMetricValue(valor, 'usd')
}

export function MrrLinea({ trend, alto = 168 }: MrrTrendProps) {
  if (trend.length < 2) {
    return (
      <Vacio icono={null}
        mensaje="La serie de MRR todavía no tiene dos meses con dato"
        detalle="Un solo punto no es una tendencia: cargá el mes anterior para poder comparar."
      />
    )
  }

  const datos = trend.map((p) => ({ ...p, etiqueta: formatPeriodoCorto(p.periodo) }))

  return (
    <figure className="m-0">
      <figcaption className="rotulo mb-1.5">
        MRR mes a mes · {formatPeriodo(trend[0]?.periodo ?? '')} a{' '}
        {formatPeriodo(trend[trend.length - 1]?.periodo ?? '')}
      </figcaption>
      <div style={{ height: alto }} data-testid="mrr-linea">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={datos} margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis
              dataKey="etiqueta"
              tick={EJE}
              stroke={CHART.grid}
              tickLine={false}
            />
            <YAxis
              tick={EJE}
              stroke={CHART.grid}
              tickLine={false}
              width={64}
              tickFormatter={usd}
            />
            <Tooltip
              formatter={(valor: number) => [usd(valor), 'MRR']}
              labelFormatter={(etiqueta: string) => etiqueta}
            />
            <Line
              type="monotone"
              dataKey="mrr"
              stroke={CHART.serie}
              strokeWidth={2}
              dot={{ r: 2, fill: CHART.serie }}
              activeDot={{ r: 4 }}
              // Sin animación: el panel se abre para leer un número, no para
              // mirar una transición.
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </figure>
  )
}

export function NetNewBarras({ trend, alto = 196 }: MrrTrendProps) {
  if (trend.length === 0) {
    return (
      <Vacio icono={null}
        mensaje="Todavía no hay meses con MRR cargado"
        detalle="La descomposición en nuevo y perdido sale de la carga mensual de revenue."
      />
    )
  }

  // La pérdida se dibuja negativa: un mes de bajas para arriba se lee como
  // crecimiento.
  const datos = trend.map((p) => ({
    etiqueta: formatPeriodoCorto(p.periodo),
    nuevo: p.nuevoMrr,
    perdido: -p.churnedMrr,
  }))

  return (
    <figure className="m-0">
      <div style={{ height: alto }} data-testid="net-new-barras">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={datos} margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis
              dataKey="etiqueta"
              tick={EJE}
              stroke={CHART.grid}
              tickLine={false}
            />
            <YAxis
              tick={EJE}
              stroke={CHART.grid}
              tickLine={false}
              width={64}
              tickFormatter={usd}
            />
            <ReferenceLine y={0} stroke={CHART.eje} />
            <Tooltip
              formatter={(valor: number, nombre: string) => [
                usd(Math.abs(valor)),
                nombre === 'nuevo' ? 'MRR nuevo' : 'MRR perdido',
              ]}
            />
            <Bar
              dataKey="nuevo"
              stackId="neto"
              fill={CHART.serieSuave}
              isAnimationActive={false}
            />
            <Bar
              dataKey="perdido"
              stackId="neto"
              fill={CHART.perdida}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* La leyenda va en texto y no como color a secas. */}
      <figcaption className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-micro text-muted-strong">
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-3 w-3 rounded-[2px] bg-accent shadow-[inset_0_0_0_1px_rgba(0,0,0,.32)]"
          />
          MRR nuevo, arriba del eje
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="h-3 w-3 rounded-[2px] bg-danger shadow-[inset_0_0_0_1px_rgba(0,0,0,.32)]"
          />
          MRR perdido, abajo del eje
        </span>
      </figcaption>
    </figure>
  )
}
